"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Photo } from "@/lib/immich/types";

export interface PhotoDeepLinkApi {
  readonly openIndex: number | null;
  readonly isOpen: boolean;
  readonly handleOpen: (index: number) => void;
  readonly handleClose: () => void;
  readonly handleIndexChange: (next: number) => void;
}

/**
 * Path-based deep linking for the photo modal.
 *
 * - Initial open state derives from `initialPhotoId` (usually the second
 *   path segment, resolved server-side).
 * - Opening / closing / navigating writes the new path via
 *   `history.replaceState` so Next.js doesn't re-render the page.
 * - A `popstate` listener keeps modal state in sync with browser back/forward.
 */
export function usePhotoDeepLink(
  photos: readonly Photo[],
  basePath: string,
  initialPhotoId: string | undefined,
): PhotoDeepLinkApi {
  const initialIndex = useMemo(() => {
    if (!initialPhotoId) return null;
    const idx = photos.findIndex((p) => p.id === initialPhotoId);
    return idx === -1 ? null : idx;
  }, [initialPhotoId, photos]);

  const [openIndex, setOpenIndex] = useState<number | null>(initialIndex);

  const syncUrl = useCallback(
    (photoId: string | null) => {
      if (typeof window === "undefined") return;
      const url = photoId ? `${basePath}/${photoId}` : basePath;
      if (window.location.pathname !== url) {
        window.history.replaceState(null, "", url);
      }
    },
    [basePath],
  );

  const handleOpen = useCallback(
    (index: number) => {
      setOpenIndex(index);
      syncUrl(photos[index]?.id ?? null);
    },
    [photos, syncUrl],
  );
  const handleClose = useCallback(() => {
    setOpenIndex(null);
    syncUrl(null);
  }, [syncUrl]);
  const handleIndexChange = useCallback(
    (next: number) => {
      setOpenIndex(next);
      syncUrl(photos[next]?.id ?? null);
    },
    [photos, syncUrl],
  );

  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      const suffix = path.startsWith(`${basePath}/`)
        ? path.slice(basePath.length + 1)
        : null;
      if (!suffix) {
        setOpenIndex(null);
        return;
      }
      const idx = photos.findIndex((p) => p.id === suffix);
      setOpenIndex(idx === -1 ? null : idx);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [basePath, photos]);

  return {
    openIndex,
    isOpen: openIndex !== null,
    handleOpen,
    handleClose,
    handleIndexChange,
  };
}
